import type { Core, ApplePay, ResultCode, SubmitActions } from '@adyen/adyen-web';
import { createAdyenApplePayConfiguration, getApplePayComponentOptions } from '@/utils/adyen';
import type { Customer } from '@/types/checkout';
import { getLocale } from '@/utils/get-locale-util';
import type { AdyenApplePayPaymentMethod, CreateApplePayComponentParams } from '@/types/adyen';
import { Errors } from '@/constants/errors';

class AdyenApplePayService {
  private checkoutInstance: Core | null = null;
  private isInitializing = false;
  private applePayActions: SubmitActions | null = null;

  setApplePayActions(actions: SubmitActions): void {
    this.applePayActions = actions;
  }

  completeApplePayPayment(success: boolean, resultCode?: ResultCode): void {
    if (this.applePayActions) {
      if (success && resultCode) {
        this.applePayActions.resolve({ resultCode });
      } else {
        this.applePayActions.reject();
      }
      this.applePayActions = null;
    }
  }

  async initializeCheckoutInstance(
    applePayPaymentMethod: AdyenApplePayPaymentMethod,
    customer?: Customer
  ): Promise<Core> {
    if (this.checkoutInstance) {
      return this.checkoutInstance;
    }

    if (this.isInitializing) {
      return this.initializeCheckoutInstance(applePayPaymentMethod, customer);
    }

    this.isInitializing = true;

    try {
      const configuration = createAdyenApplePayConfiguration(applePayPaymentMethod, customer);
      const { pspLocale } = getLocale();
      const fullConfig = { ...configuration, locale: pspLocale };

      if (!fullConfig.environment) {
        throw new Error('Adyen environment is required (test or live)');
      }

      const adyenWeb = await import('@adyen/adyen-web');
      const AdyenCheckoutClass = adyenWeb.AdyenCheckout || (adyenWeb as Record<string, unknown>).default;

      if (!AdyenCheckoutClass || typeof AdyenCheckoutClass !== 'function') {
        throw new Error(Errors.payment.failedToImportAdyenCheckout);
      }

      const instance = await AdyenCheckoutClass(fullConfig);
      this.checkoutInstance = instance;
      return this.checkoutInstance;
    } finally {
      this.isInitializing = false;
    }
  }

  async createApplePayComponent(params: CreateApplePayComponentParams): Promise<ApplePay | null> {
    if (!this.checkoutInstance) return null;

    const options = getApplePayComponentOptions(params);

    try {
      const { ApplePay } = await import('@adyen/adyen-web');

      const component = new ApplePay(this.checkoutInstance, {
        ...options,
      } as never);
      return component;
    } catch (error) {
      console.error('Failed to create Apple Pay component:', error);
      return null;
    }
  }

  reset(): void {
    this.checkoutInstance = null;
    this.isInitializing = false;
    this.applePayActions = null;
  }
}

export const adyenApplePayService = new AdyenApplePayService();
